import React, { useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import {
  NavLink,
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import {
  palette,
  radius,
  shadow,
  spacing,
  status,
  transition,
  typography,
  layout,
} from '../../style-utils/theme';
import { down, touch } from '../../style-utils/media-queries';
import { useAuth } from '../../context/auth-provider';
import { resolvePostAuthRoute } from '../../utils/post-auth-route';
import { useDocumentTitle } from '../hooks/use-document-title';
import { BackBar, BackButton } from '../layout/back-button';
import { Shell } from '../onboarding/onboarding.styled';
import { LogoMark } from '../logo/logo-mark';
import { LEGAL_DOCS, STAYS_LEGAL_DOCS, findLegalDoc, isStaysLegal, legalHref, policyHtml } from './legal-docs';
import { FaqBody } from './faq-body';
import { useStaysProduct } from '../../utils/app-features';

/**
 * /legal/:doc — the policies, inside the app.
 *
 * Reachable signed OUT (from sign-in and signup) and signed IN (from the account
 * page), so it is a public route that also renders for a session — see
 * PUBLIC_PREFIXES / SIGNED_IN_PUBLIC_PREFIXES in utils/post-auth-route.ts.
 *
 * The text is lobbero_web's, copied by scripts/sync-legal.mjs; nothing here
 * rewords it. The styles are the web's doc.css re-expressed in the product's
 * theme tokens, so the policy reads like the policy on lobbero.com but sits in
 * the app's own type and colour.
 */

const Frame = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: ${layout.contentMaxWidth};
  display: flex;
  flex-direction: column;
`;

const Brand = styled.span`
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: ${spacing.xs};
  font-family: ${typography.display};
  font-size: ${typography.size.lg}px;
  font-weight: ${typography.weight.heavy};
  letter-spacing: ${typography.tracking.tight};
  color: ${palette.ink};
`;

const DocSwitch = styled.nav`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing.xs};
  margin-bottom: ${spacing.md};

  & a {
    display: inline-flex;
    align-items: center;
    min-height: ${layout.touchTarget};
    padding: 0 ${spacing.sm};
    border-radius: ${radius.pill};
    border: 1px solid ${palette.line};
    background: ${palette.surface};
    color: ${palette.inkSoft};
    font-family: ${typography.ui};
    font-size: ${typography.size.sm}px;
    font-weight: ${typography.weight.medium};
    text-decoration: none;
    transition: ${transition.colors};
  }

  & a:hover {
    border-color: ${palette.lineStrong};
    color: ${palette.ink};
  }

  & a:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }

  & a[aria-current='page'] {
    background: ${palette.irisTint};
    border-color: ${palette.iris};
    color: ${status.brand.fg};
    font-weight: ${typography.weight.semibold};
  }

  /* Wrapped, five pills took four rows of a phone before the document began.
     One row that scrolls sideways inside itself instead — the page does not. */
  ${down('md')} {
    flex-wrap: nowrap;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    margin-left: -${spacing.sm};
    margin-right: -${spacing.sm};
    padding: 0 ${spacing.sm} 2px;

    &::-webkit-scrollbar {
      display: none;
    }

    & a {
      flex: 0 0 auto;
      white-space: nowrap;
    }
  }
`;

const Paper = styled.div`
  background: ${palette.surface};
  border: 1px solid ${palette.line};
  border-radius: ${radius.lg};
  box-shadow: ${shadow.md};
  /* NO overflow: hidden. It would make this the contents rail's scroll
     container and the sticky contents list would stop sticking. */

  ${down('sm')} {
    border-radius: ${radius.md};
  }
`;

/* doc.css from lobbero_web, scoped to this wrapper and moved onto the theme.
   Selectors mirror the web's class names because the HTML is the web's. */
const DocBody = styled.div`
  font-family: ${typography.ui};
  font-size: ${typography.size.base}px;
  line-height: ${typography.leading.relaxed};
  color: ${palette.ink};

  & *,
  & *::before,
  & *::after {
    box-sizing: border-box;
  }

  & h1,
  & h2,
  & h3,
  & p,
  & ul,
  & ol {
    margin: 0;
    padding: 0;
  }

  & a:not(.btn) {
    color: ${palette.lagoon};
    text-decoration: none;
  }

  & a:not(.btn):hover {
    text-decoration: underline;
  }

  & a:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
    border-radius: 4px;
  }

  & .wrap {
    padding-left: ${spacing.xl};
    padding-right: ${spacing.xl};
  }

  /* ---------- Title block ---------- */
  & .hero {
    padding-top: ${spacing.xxl};
    padding-bottom: ${spacing.lg};
    max-width: 860px;
  }

  & .eyebrow {
    font-family: ${typography.mono};
    font-size: ${typography.size.xxs}px;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: ${palette.lagoon};
    margin-bottom: 14px;
  }

  & h1 {
    font-family: ${typography.display};
    font-weight: ${typography.weight.bold};
    font-size: clamp(2rem, 5vw, 2.9rem);
    line-height: ${typography.leading.tight};
    letter-spacing: ${typography.tracking.tight};
    margin-bottom: 18px;
    text-wrap: balance;
  }

  & .lede {
    font-size: ${typography.size.md}px;
    line-height: 1.6;
    color: ${palette.slate};
    max-width: 62ch;
  }

  & .meta {
    display: flex;
    flex-wrap: wrap;
    gap: 8px 26px;
    margin-top: 26px;
    padding-top: 20px;
    border-top: 1px solid ${palette.line};
    font-family: ${typography.mono};
    font-size: 12.5px;
    color: ${palette.slate};
  }

  & .meta b {
    color: ${palette.ink};
    font-weight: ${typography.weight.medium};
  }

  /* ---------- The "pending review" banner ----------
     Body copy in inkSoft rather than the web's amber text: the product's
     amber (status.warning.fg) measures under 4.5:1 on its own soft fill, so
     amber carries the icon and the lead-in, and ink carries the reading. */
  & .review {
    background: ${palette.warningSoft};
    border-top: 1px solid ${status.warning.dot}33;
    border-bottom: 1px solid ${status.warning.dot}33;
  }

  & .review-in {
    padding: ${spacing.sm} ${spacing.xl};
    display: flex;
    gap: 14px;
    align-items: flex-start;
    font-size: 14.5px;
    line-height: 1.55;
    color: ${palette.inkSoft};
  }

  & .review-in svg {
    width: 20px;
    height: 20px;
    flex: 0 0 auto;
    margin-top: 3px;
    color: ${status.warning.fg};
  }

  & .review-in b {
    color: ${palette.ink};
  }

  & .ph {
    font-family: ${typography.mono};
    font-size: 0.92em;
    background: ${palette.warningSoft};
    color: ${palette.inkSoft};
    border: 1px dashed ${status.warning.dot};
    border-radius: 5px;
    padding: 1px 6px;
    overflow-wrap: break-word;
    word-break: normal;
  }

  /* ---------- Two-column body ---------- */
  & .grid {
    display: grid;
    grid-template-columns: 236px minmax(0, 1fr);
    gap: ${spacing.xxl};
    align-items: start;
    padding-top: ${spacing.xl};
    padding-bottom: ${spacing.xxxl};
  }

  /* min-width: 0 — a wide table must scroll in its wrapper, not widen the
     column (see the same note in lobbero_web/styles/doc.css). */
  & .grid > * {
    min-width: 0;
  }

  & .toc {
    position: sticky;
    top: ${spacing.md};
    font-size: ${typography.size.sm}px;
  }

  & .toc h2 {
    font-family: ${typography.mono};
    font-size: 11px;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: ${palette.slate};
    font-weight: ${typography.weight.medium};
    margin: 0 0 12px;
    border: 0;
    padding: 0;
  }

  & .toc ol {
    list-style: none;
    border-left: 1px solid ${palette.line};
  }

  & .toc a {
    display: block;
    padding: 7px 12px;
    color: ${palette.slate};
    border-left: 2px solid transparent;
    margin-left: -1px;
  }

  & .toc a:hover {
    color: ${palette.ink};
    border-left-color: ${palette.iris};
    text-decoration: none;
  }

  /* An iPad held sideways is 1024px wide: the contents list is still the
     desktop rail there, but under a finger. Grow its links to the 44px floor
     on touch screens only; a mouse keeps the tighter list. */
  ${touch} {
    & .toc a {
      min-height: ${layout.touchTarget};
      display: flex;
      align-items: center;
    }
  }

  & article > section {
    padding: 0 0 12px;
    scroll-margin-top: ${spacing.md};
  }

  & h2 {
    font-family: ${typography.display};
    font-weight: ${typography.weight.bold};
    font-size: 1.6rem;
    line-height: 1.2;
    letter-spacing: ${typography.tracking.snug};
    margin: 40px 0 14px;
  }

  & h2.sec {
    display: flex;
    align-items: baseline;
    gap: 14px;
    padding-top: 26px;
    border-top: 1px solid ${palette.line};
  }

  & h2.sec .n {
    font-family: ${typography.mono};
    font-size: 12px;
    font-weight: ${typography.weight.medium};
    letter-spacing: 0.08em;
    color: ${palette.lagoon};
    flex: 0 0 auto;
  }

  & h3 {
    font-weight: ${typography.weight.semibold};
    font-size: 1.05rem;
    margin: 26px 0 8px;
  }

  & p,
  & li {
    max-width: 74ch;
  }

  & article p {
    margin-bottom: 14px;
    color: ${palette.slate};
  }

  & article p strong,
  & article li strong {
    color: ${palette.ink};
    font-weight: ${typography.weight.semibold};
  }

  & article ul,
  & article ol {
    margin: 0 0 16px 20px;
    color: ${palette.slate};
  }

  & article li {
    margin-bottom: 8px;
  }

  & .term {
    color: ${palette.ink};
    font-weight: ${typography.weight.semibold};
    box-shadow: inset 0 -0.42em 0 ${palette.tealWashSoft};
  }

  /* ---------- Callouts ---------- */
  & .glance {
    background: ${palette.irisTintSoft};
    border: 1px solid ${palette.irisTint};
    border-radius: ${radius.md};
    padding: 24px 26px;
    margin-bottom: 8px;
  }

  & .glance h2 {
    margin: 0 0 12px;
    font-size: 1.35rem;
    border: 0;
    padding: 0;
  }

  & .glance ul {
    margin: 0 0 0 20px;
  }

  & .glance li {
    color: ${palette.ink};
  }

  & .box {
    background: ${palette.surfaceMuted};
    border: 1px solid ${palette.line};
    border-radius: 14px;
    padding: 18px 20px;
    margin: 0 0 16px;
  }

  & .box p {
    margin-bottom: 8px;
  }

  & .box p:last-child {
    margin-bottom: 0;
  }

  & .box p.k {
    font-weight: ${typography.weight.semibold};
    font-size: 14.5px;
    color: ${palette.ink};
  }

  & .box.warn {
    background: ${palette.warningSoft};
    border-color: ${status.warning.dot}55;
  }

  & .box.warn p {
    color: ${palette.inkSoft};
  }

  /* ---------- Tables: scroll inside their own wrapper, never clip ---------- */
  & .tw {
    border: 1px solid ${palette.line};
    border-radius: 14px;
    background: ${palette.surface};
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    margin: 0 0 18px;
  }

  & .tw table {
    border-collapse: collapse;
    width: 100%;
    min-width: 620px;
    font-size: ${typography.size.sm}px;
  }

  & .tw th,
  & .tw td {
    padding: 12px 16px;
    text-align: left;
    vertical-align: top;
    border-bottom: 1px solid ${palette.line};
  }

  & .tw thead th {
    font-family: ${typography.mono};
    font-size: 11px;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    font-weight: ${typography.weight.medium};
    color: ${palette.slate};
    background: ${palette.surfaceMuted};
    white-space: nowrap;
  }

  & .tw tbody tr:last-child td {
    border-bottom: 0;
  }

  & .tw td {
    color: ${palette.slate};
  }

  & .tw td:first-of-type {
    color: ${palette.ink};
    font-weight: ${typography.weight.medium};
  }

  & .lb {
    display: inline-block;
    font-family: ${typography.mono};
    font-size: 10.5px;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: ${status.brand.fg};
    background: ${palette.irisTint};
    border-radius: 5px;
    padding: 2px 7px;
    white-space: nowrap;
  }

  /* ---------- Control grid (Security "at a glance") ---------- */
  & .controls {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(230px, 1fr));
    gap: 14px;
    margin: 0 0 18px;
  }

  & .ctl {
    background: ${palette.surface};
    border: 1px solid ${palette.line};
    border-radius: 14px;
    padding: 18px;
  }

  & .ctl .ico {
    width: 38px;
    height: 38px;
    border-radius: 11px;
    background: ${palette.irisTint};
    color: ${palette.lagoon};
    display: grid;
    place-items: center;
    margin-bottom: 12px;
  }

  & .ctl .ico svg {
    width: 19px;
    height: 19px;
    display: block;
  }

  & .ctl b {
    display: block;
    font-size: 14.5px;
    color: ${palette.ink};
    margin-bottom: 4px;
  }

  & .ctl > span {
    display: block;
    font-size: 13.5px;
    line-height: 1.55;
    color: ${palette.slate};
  }

  /* ---------- Rights cards ---------- */
  & .rights {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 14px;
    margin: 0 0 18px;
  }

  & .rc {
    background: ${palette.surface};
    border: 1px solid ${palette.line};
    border-radius: ${radius.sm};
    padding: 16px 18px;
  }

  & .rc b {
    display: block;
    font-size: 14.5px;
    margin-bottom: 4px;
    color: ${palette.ink};
  }

  & .rc span {
    font-size: 13.5px;
    color: ${palette.slate};
  }

  /* ---------- End-of-document contact block ---------- */
  & .fl {
    list-style: none;
    margin: 0 0 16px;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  & .fl li {
    font-size: 15px;
  }

  & .doc-cta {
    margin: 40px 0 0;
    background: ${palette.surfaceMuted};
    border: 1px solid ${palette.line};
    border-radius: 18px;
    padding: 28px;
    text-align: center;
  }

  & .doc-cta h2 {
    margin: 0 0 6px;
    border: 0;
    padding: 0;
    font-size: 1.4rem;
  }

  & .doc-cta p {
    margin: 0 auto 18px;
    max-width: 48ch;
  }

  & .doc-cta .row {
    display: flex;
    gap: 12px;
    justify-content: center;
    flex-wrap: wrap;
  }

  & .btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: ${layout.touchTarget};
    padding: 0 ${spacing.md};
    border-radius: ${radius.pill};
    font-size: ${typography.size.sm}px;
    font-weight: ${typography.weight.semibold};
    text-decoration: none;
    transition: ${transition.colors};
  }

  & .btn-primary {
    background: ${palette.lagoon};
    color: ${palette.white};
    box-shadow: ${shadow.sm};
  }

  & .btn-primary:hover {
    background: ${palette.irisDeep};
  }

  & .btn-ghost {
    background: ${palette.surface};
    border: 1px solid ${palette.lineStrong};
    color: ${palette.ink};
  }

  & .btn-ghost:hover {
    border-color: ${palette.lagoon};
  }

  ${down('lg')} {
    & .grid {
      grid-template-columns: minmax(0, 1fr);
      gap: 28px;
    }

    /* The contents list stops being a rail; sticky in one column would pin
       it over the text. */
    & .toc {
      position: static;
      background: ${palette.surface};
      border: 1px solid ${palette.line};
      border-radius: 14px;
      padding: 16px 18px;
    }

    & .toc ol {
      border-left: 0;
      columns: 2;
      column-gap: 20px;
    }

    & .toc a {
      padding: 8px 0;
      border-left: 0;
      min-height: ${layout.touchTarget};
      display: flex;
      align-items: center;
    }

    & .toc a:hover {
      border-left: 0;
    }
  }

  ${down('sm')} {
    & .wrap {
      padding-left: ${spacing.md};
      padding-right: ${spacing.md};
    }

    & .review-in {
      padding: ${spacing.sm} ${spacing.md};
    }

    & .hero {
      padding-top: ${spacing.lg};
    }

    & .toc ol {
      columns: 1;
    }

    & h2 {
      font-size: 1.4rem;
    }

    & h2.sec {
      flex-direction: column;
      gap: 4px;
    }
  }
`;

const scrollToId = (id: string, smooth: boolean) => {
  const target = id ? document.getElementById(id) : null;
  if (!target) return false;
  target.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto', block: 'start' });

  return true;
};

export const LegalPage: React.FC = () => {
  const { doc: slug } = useParams();
  const doc = findLegalDoc(slug);
  const { user, onboarding, permissions } = useAuth();
  const navigate = useNavigate();
  const { hash } = useLocation();
  const staysProduct = useStaysProduct();

  useDocumentTitle(doc?.title);
  const switchRef = useRef<HTMLElement>(null);

  // On a phone the switcher is one sideways-scrolling row, and the later
  // documents (DPA, Security, FAQ) start off-screen. Bring the current one
  // into view. scrollLeft, not scrollIntoView: that would scroll the page too.
  useEffect(() => {
    const nav = switchRef.current;
    const current = nav?.querySelector<HTMLElement>('[aria-current="page"]');
    if (!nav || !current || nav.scrollWidth <= nav.clientWidth) return;
    nav.scrollLeft =
      current.offsetLeft - nav.clientWidth / 2 + current.offsetWidth / 2;
  }, [doc]);

  // A new document opens at its top, or at the section a cross-link named
  // (/legal/terms#law from the DPA).
  useEffect(() => {
    if (!doc) return;
    if (!scrollToId(decodeURIComponent(hash.slice(1)), false)) {
      window.scrollTo(0, 0);
    }
  }, [doc, hash]);

  // IS8.0: with Lobbero Stays switched off on the server its two documents are
  // not there — an unknown document. Nothing is drawn until the server answers.
  const staysDoc = !!doc && isStaysLegal(doc.slug);
  if (staysDoc && staysProduct === null) return null;
  if (!doc || (staysDoc && !staysProduct)) return <Navigate to={legalHref('privacy')} replace />;
  const html = policyHtml(doc.slug);
  // The two Lobbero Stays documents (IS3) switch between themselves only.
  const stays = isStaysLegal(doc.slug);
  const switchDocs = stays ? STAYS_LEGAL_DOCS : LEGAL_DOCS;

  /* The HTML is the web's, so its links are plain anchors. Two kinds are taken
     over here: a link to another policy goes through the router instead of
     reloading the app, and a contents link scrolls without handing a bare
     #fragment to the router. mailto: and external links are left alone. */
  const onDocClick = (event: React.MouseEvent<HTMLElement>) => {
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }
    const anchor = (event.target as HTMLElement).closest('a');
    const href = anchor?.getAttribute('href');
    if (!anchor || !href || anchor.target === '_blank') return;

    if (href.startsWith('#')) {
      if (scrollToId(decodeURIComponent(href.slice(1)), true)) {
        event.preventDefault();
      }

      return;
    }
    if (href.startsWith('/legal/')) {
      event.preventDefault();
      navigate(href);
    }
  };

  const back = user
    ? {
        to: resolvePostAuthRoute(user, onboarding, permissions),
        label: 'Back to Lobbero',
      }
    : { to: '/', label: 'Back to sign in' };

  return (
    /* overflow-x: clip, not the Shell's hidden. hidden makes the Shell a scroll
       container, and the sticky contents rail then sticks to a box that never
       scrolls — i.e. not at all. clip trims the same overflow without that.
       (Safari < 16 ignores clip and keeps hidden: the rail just doesn't stick.) */
    <Shell style={{ overflowX: 'clip' }}>
      <Frame>
        <BackBar>
          <BackButton to={back.to} label={back.label} />
          <Brand>
            <LogoMark size={28} aria-hidden="true" />
            {stays ? 'Lobbero Stays' : 'Lobbero'}
          </Brand>
        </BackBar>
        <DocSwitch ref={switchRef} aria-label="Legal documents">
          {switchDocs.map(item => (
            <NavLink key={item.slug} to={legalHref(item.slug)}>
              {/* The FAQ's title is a sentence; its pill says FAQ. */}
              {item.slug === 'faq' ? item.short : item.title}
            </NavLink>
          ))}
        </DocSwitch>
        {/* One click handler for both bodies: FAQ answers link to the policies
            too, and those links should stay in the app the same way. */}
        <Paper data-legal-doc={doc.slug} onClick={onDocClick}>
          {html === undefined ? (
            <FaqBody />
          ) : (
            /* Trusted, build-time content: scripts/sync-legal.mjs refuses any
               page with a script, an inline handler or a javascript: URL. */
            <DocBody className="doc" dangerouslySetInnerHTML={{ __html: html }} />
          )}
        </Paper>
      </Frame>
    </Shell>
  );
};
